import { useMemo, useState } from 'react'

import {
  faCaretDown,
  faCaretUp,
  faGear,
  faPaperPlaneTop,
} from '@document-crunch/pro-regular-svg-icons'

import { Button } from '@dc-shared-web/components/shadcn'
import { cn } from '@dc-shared-web/lib'
import { Icon, IconColor, Tooltip } from '@dc-shared-web/components/base'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'
import { useChat } from '@dc-shared-web/providers/ChatProvider'

import { documentNeedsRecrunch } from '@dc-shared-web/components/features/Chat/helpers'
import AutoSizeTextArea from '@dc-shared-web/components/base/AutoSizeTextArea'

const ChatInput = ({
  showSuggestions,
  onSubmit,
  onToggleSuggestions,
  onOpenSettings,
  isReceiving,
}: {
  showSuggestions: boolean
  onSubmit: (text: string) => void
  onToggleSuggestions: () => void
  onOpenSettings: () => void
  isReceiving: boolean
}) => {
  const { user } = useAuth()
  const { errors } = useChat()
  const [focused, setFocused] = useState(false)
  const [value, setValue] = useState('')

  const handleSubmit = () => {
    if (value && !isReceiving) {
      onSubmit(value)
      setValue('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const needsRecrunch = useMemo(() => {
    return documentNeedsRecrunch(errors)
  }, [errors])
  const showDebug = useMemo(() => user?.is_super_admin, [user])

  return (
    <div className='tw-px-2'>
      <div
        className={cn(
          'tw-border-[1px] tw-border-transparent tw-p-2 tw-rounded tw-flex tw-items-end tw-shadow-[0px_0px_10px_0px_rgba(24,103,190,0.2)]',
          focused && 'tw-border-blue-600'
        )}
      >
        <Tooltip
          content={showSuggestions ? 'Hide suggestions' : 'Show suggestions'}
          side='top'
          withArrow
        >
          <Button
            variant='gray'
            className={cn(
              'tw-h-[19.5px] tw-p-0 tw-px-1.5 hover:tw-bg-blue-600 tw-group tw-py-3',
              showSuggestions && 'tw-bg-blue-600'
            )}
            onClick={onToggleSuggestions}
          >
            <Icon
              icon={showSuggestions ? faCaretDown : faCaretUp}
              className={cn(
                'group-hover:tw-text-white',
                showSuggestions && 'tw-text-white'
              )}
            />
          </Button>
        </Tooltip>
        <AutoSizeTextArea
          placeholder={`What's your question?`}
          className='tw-grow tw-mx-1 tw-border-none focus-visible:tw-ring-0 tw-min-h-[0px] tw-p-0 tw-resize-none'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          data-testid='chat-input'
          maxRows={15}
        />
        {showDebug && (
          <Button
            className='tw-h-[19.5px] tw-p-0 tw-px-1.5 tw-bg-blue-600 hover:tw-bg-blue-500 tw-py-3'
            onClick={onOpenSettings}
          >
            <Icon icon={faGear} size='sm' color={IconColor.white} />
          </Button>
        )}

        <Tooltip
          content={isReceiving ? 'Receiving response...' : 'Submit question'}
          side='top'
          withArrow
        >
          <Button
            className={
              'tw-h-[19.5px] tw-p-0 tw-px-1.5 tw-ml-1 tw-bg-blue-600 hover:tw-bg-blue-500 disabled:tw-bg-transparent tw-group tw-py-3'
            }
            disabled={(!value && !needsRecrunch) || isReceiving}
            onClick={handleSubmit}
            data-testid='chat-send-button'
          >
            <Icon
              icon={faPaperPlaneTop}
              size='sm'
              color={IconColor.white}
              className='group-disabled:tw-text-neutral-700'
            />
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}

export default ChatInput
